import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

export interface FetchOptionsResult {
  items: SelectOption[];
  hasMore: boolean;
}

interface SelectBase {
  /** Статичный список — используется когда fetchOptions не передан */
  options?: SelectOption[];
  name?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Инлайн стили для кнопки-триггера */
  triggerStyle?: React.CSSProperties;
  /**
   * Показать поле поиска для фильтрации статичных опций на клиенте.
   * При fetchOptions поиск включён автоматически.
   */
  searchable?: boolean;
  /**
   * Асинхронная подгрузка данных с бэка.
   * Вызывается при открытии, изменении поиска и скролле к концу списка.
   */
  fetchOptions?: (params: { page: number; search: string }) => Promise<FetchOptionsResult>;
  /**
   * Загрузка конкретных опций по их value (ID).
   * Вызывается при маунте если value уже задан — чтобы показать лейбл, а не ID.
   */
  fetchByValues?: (values: string[]) => Promise<SelectOption[]>;
  renderOption?: (option: SelectOption, state: { selected: boolean }) => React.ReactNode;
  renderValue?: (option: SelectOption | null) => React.ReactNode;
  searchPlaceholder?: string;
  /** Render dropdown via portal to avoid overflow clipping */
  portal?: boolean;
}

interface SingleSelect extends SelectBase {
  mode?: 'single';
  value: string;
  onChange: (value: string, option?: SelectOption | null) => void;
}

interface MultiSelect extends SelectBase {
  mode: 'multi';
  value: string[];
  onChange: (value: string[]) => void;
}

type SelectProps = SingleSelect | MultiSelect;

const SCROLL_THRESHOLD = 48;

const Select: React.FC<SelectProps> = (props) => {
  const {
    options: staticOptions = [],
    placeholder = 'Выберите...',
    icon,
    disabled,
    className = '',
    fetchOptions,
    fetchByValues,
    triggerStyle,
    searchable = false,
    renderOption,
    renderValue,
    searchPlaceholder = 'Поиск...',
    name,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // --- async state ---
  const [asyncItems, setAsyncItems] = useState<SelectOption[]>([]);
  const [asyncPage, setAsyncPage] = useState(1);
  const [asyncHasMore, setAsyncHasMore] = useState(false);
  const [asyncLoading, setAsyncLoading] = useState(false);

  // Порядок закреплённых сверху выбранных фиксируется при открытии,
  // чтобы выбор на телефоне не перетасовывал список и не «прыгал» скролл.
  const frozenOrderRef = useRef<string[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingRef = useRef(false);

  const isAsync = Boolean(fetchOptions);
  const usePortal = Boolean(props.portal);
  const isMulti = props.mode === 'multi';
  const showSearch = isAsync || searchable;

  // Видимые опции: для async — загруженные, для статик — отфильтрованные
  const rawVisible: SelectOption[] = isAsync
    ? asyncItems
    : searchable && search.trim()
      ? staticOptions.filter(o =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : staticOptions;

  // Для multi без поиска — выбранные всегда сверху
  const visibleOptions: SelectOption[] = isMulti && !search.trim()
    ? (() => {
        // Закреплённый порядок с момента открытия — новые выборы не всплывают вверх.
        const selectedValues = frozenOrderRef.current;
        // Для async выбранные могут не попасть в текущую страницу —
        // ищем их в staticOptions (knownOptions) или в asyncItems
        const allKnown = isAsync
          ? [...staticOptions, ...asyncItems]
          : rawVisible;
        const seen = new Set<string>();
        const pinned: SelectOption[] = [];
        selectedValues.forEach(v => {
          const opt = allKnown.find(o => o.value === v);
          if (opt && !seen.has(v)) { seen.add(v); pinned.push(opt); }
        });
        const rest = rawVisible.filter(o => !seen.has(o.value));
        return [...pinned, ...rest];
      })()
    : rawVisible;

  /* ------------------------------------------------------------------ */
  /*  Async loading                                                        */
  /* ------------------------------------------------------------------ */

  const loadPage = useCallback(async (page: number, term: string, replace: boolean) => {
    if (!fetchOptions || loadingRef.current) return;
    loadingRef.current = true;
    setAsyncLoading(true);
    try {
      const result = await fetchOptions({ page, search: term });
      setAsyncItems(prev => replace ? result.items : [...prev, ...result.items]);
      setAsyncHasMore(result.hasMore);
      setAsyncPage(page);
    } catch {
      // ignore
    } finally {
      setAsyncLoading(false);
      loadingRef.current = false;
    }
  }, [fetchOptions]);

  // При маунте — загружаем опции по ID чтобы показать лейбл вместо сырого значения
  useEffect(() => {
    if (!fetchByValues || !hasValue) return;
    const values = isMulti
      ? (props as MultiSelect).value
      : [(props as SingleSelect).value];
    fetchByValues(values).then(items => setAsyncItems(items));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !usePortal || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPortalPos({ top: rect.bottom, left: rect.left, width: rect.width });
  }, [open, usePortal]);

  // On open — сброс поиска + загрузка первой страницы (async) / фокус (любой)
  useEffect(() => {
    if (!open) return;
    setSearch('');
    if (isAsync) {
      setAsyncItems([]);
      setAsyncHasMore(false);
      loadPage(1, '', true);
    }
    if (showSearch) {
      setTimeout(() => searchRef.current?.focus(), 30);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (term: string) => {
    setSearch(term);
    if (!isAsync) return; // локальный поиск — просто обновляем строку
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setAsyncItems([]);
      loadPage(1, term, true);
    }, 300);
  };

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el || !asyncHasMore || asyncLoading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
      loadPage(asyncPage + 1, search, false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Outside click / Escape                                               */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(target) &&
        (!dropRef.current || !dropRef.current.contains(target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  /* ------------------------------------------------------------------ */
  /*  Value helpers                                                        */
  /* ------------------------------------------------------------------ */

  const isChecked = (optValue: string) =>
    isMulti
      ? (props as MultiSelect).value.includes(optValue)
      : (props as SingleSelect).value === optValue;

  const hasValue = isMulti
    ? (props as MultiSelect).value.length > 0
    : Boolean((props as SingleSelect).value);

  const triggerLabel = (): string => {
    if (isMulti) {
      const selected = (props as MultiSelect).value;
      if (!selected.length) return '';
      if (selected.length === 1) {
        // Ищем лейбл во всём статик-списке, а не только в видимых
        return staticOptions.find(o => o.value === selected[0])?.label
          ?? asyncItems.find(o => o.value === selected[0])?.label
          ?? selected[0];
      }
      return `Выбрано: ${selected.length}`;
    }
    const val = (props as SingleSelect).value;
    return staticOptions.find(o => o.value === val)?.label
      ?? asyncItems.find(o => o.value === val)?.label
      ?? val ?? '';
  };

  /* ------------------------------------------------------------------ */
  /*  Handlers                                                             */
  /* ------------------------------------------------------------------ */

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMulti) (props as MultiSelect).onChange([]);
    else (props as SingleSelect).onChange('', null);
  };

  const handleOption = (optValue: string) => {
    if (isMulti) {
      const current = (props as MultiSelect).value;
      const next = current.includes(optValue)
        ? current.filter(v => v !== optValue)
        : [...current, optValue];
      (props as MultiSelect).onChange(next);
    } else {
      const current = (props as SingleSelect).value;
      const isDeselect = current === optValue;
      const opt = isDeselect ? null : (visibleOptions.find(o => o.value === optValue) ?? null);
      (props as SingleSelect).onChange(isDeselect ? '' : optValue, opt);
      setOpen(false);
    }
  };

  // «Выбрать все» — выбирает только видимые (отфильтрованные) опции
  const handleSelectAll = () => {
    const current = (props as MultiSelect).value;
    const visibleValues = visibleOptions.map(o => o.value);
    const merged = [...new Set([...current, ...visibleValues])];
    (props as MultiSelect).onChange(merged);
  };

  const handleClearAll = () => {
    (props as MultiSelect).onChange([]);
  };

  // Выбрать/снять всю группу разом (multi)
  const handleToggleGroup = (groupItems: SelectOption[]) => {
    const current = (props as MultiSelect).value;
    const vals = groupItems.filter(o => !o.disabled).map(o => o.value);
    const allSelected = vals.every(v => current.includes(v));
    const next = allSelected
      ? current.filter(v => !vals.includes(v))
      : [...new Set([...current, ...vals])];
    (props as MultiSelect).onChange(next);
  };

  const label = triggerLabel();
  const selectedOption = isMulti || !(props as SingleSelect).value
    ? null
    : staticOptions.find(o => o.value === (props as SingleSelect).value)
      ?? asyncItems.find(o => o.value === (props as SingleSelect).value)
      ?? { value: (props as SingleSelect).value, label };

  /* ------------------------------------------------------------------ */
  /*  Render                                                               */
  /* ------------------------------------------------------------------ */

  return (
    <div
      ref={wrapRef}
      className={`ui-select-wrap${className ? ` ${className}` : ''}`}
    >
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className={`ui-select-trigger${open ? ' open' : ''}${disabled ? ' disabled' : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen(p => {
            const next = !p;
            if (next && isMulti) frozenOrderRef.current = [...(props as MultiSelect).value];
            return next;
          });
        }}
        disabled={disabled}
        style={triggerStyle}
      >
        {icon && <span className="ui-select-icon">{icon}</span>}
        <span className={`ui-select-value${!label ? ' placeholder' : ''}`}>
          {!isMulti && hasValue && renderValue
            ? renderValue(selectedOption)
            : label || placeholder}
        </span>
        <span className="ui-select-actions">
          {hasValue && (
            <span className="ui-select-clear" onMouseDown={handleClear}>
              <IconX size={12} stroke={2.5} />
            </span>
          )}
          <IconChevronDown
            size={14}
            stroke={2}
            className={`ui-select-chevron${open ? ' rotated' : ''}`}
          />
        </span>
      </button>

      {/* Dropdown */}
      {open && (() => {
        const dropStyle: React.CSSProperties | undefined = usePortal && portalPos
          ? { position: 'fixed', top: portalPos.top, left: portalPos.left, width: portalPos.width, zIndex: 9999 }
          : undefined;
        const dropEl = (
        <div ref={dropRef} className="ui-select-drop" style={dropStyle}>

          {/* Поиск — показывается для async И для searchable статик */}
          {showSearch && (
            <div className="ui-select-search-wrap">
              <IconSearch size={13} stroke={2} className="ui-select-search-icon" />
              <input
                ref={searchRef}
                className="ui-select-search"
                type="text"
                name={name ? `${name}-search` : 'select-search'}
                value={search}
                onChange={e => handleSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
              {search && (
                <button
                  type="button"
                  className="ui-select-search-clear"
                  onMouseDown={e => { e.preventDefault(); handleSearch(''); }}
                >
                  <IconX size={11} stroke={2.5} />
                </button>
              )}
            </div>
          )}

          {/* Список опций */}
          <div
            className="ui-select-options"
            ref={listRef}
            onScroll={isAsync ? handleListScroll : undefined}
          >
            {(() => {
              const hasGroups = visibleOptions.some(o => o.group);
              if (!hasGroups) {
                return visibleOptions.map(opt => (
                  <div
                    key={opt.value}
                    className={`ui-select-opt${isChecked(opt.value) ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}`}
                    onMouseDown={e => { e.preventDefault(); if (!opt.disabled) handleOption(opt.value); }}
                  >
                    {isMulti ? (
                      <span className={`ui-select-checkbox${isChecked(opt.value) ? ' checked' : ''}`}>
                        {isChecked(opt.value) && <IconCheck size={10} stroke={3} />}
                      </span>
                    ) : (
                      <span className="ui-select-radio-space">
                        {isChecked(opt.value) && (
                          <IconCheck size={12} stroke={2.5} style={{ color: 'var(--orange)' }} />
                        )}
                      </span>
                    )}
                    {renderOption
                      ? renderOption(opt, { selected: isChecked(opt.value) })
                      : <span className="ui-select-opt-label">{opt.label}</span>}
                  </div>
                ));
              }
              const grouped: { group: string; items: SelectOption[] }[] = [];
              visibleOptions.forEach(opt => {
                const g = opt.group ?? '';
                const existing = grouped.find(x => x.group === g);
                if (existing) existing.items.push(opt);
                else grouped.push({ group: g, items: [opt] });
              });
              return grouped.map(({ group, items }) => (
                <div key={group} className="ui-select-group">
                  {group && (isMulti ? (() => {
                    const groupItems = items.filter(o => !o.disabled);
                    const allChecked = groupItems.length > 0 && groupItems.every(o => isChecked(o.value));
                    return (
                      <div
                        className={`ui-select-group-label ui-select-group-label--pick${allChecked ? ' checked' : ''}`}
                        onMouseDown={e => { e.preventDefault(); handleToggleGroup(items); }}
                      >
                        <span className={`ui-select-checkbox${allChecked ? ' checked' : ''}`}>
                          {allChecked && <IconCheck size={10} stroke={3} />}
                        </span>
                        {group}
                      </div>
                    );
                  })() : (
                    <div className="ui-select-group-label">{group}</div>
                  ))}
                  {items.map(opt => (
                    <div
                      key={opt.value}
                      className={`ui-select-opt${isChecked(opt.value) ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}`}
                      onMouseDown={e => { e.preventDefault(); if (!opt.disabled) handleOption(opt.value); }}
                    >
                      {isMulti ? (
                        <span className={`ui-select-checkbox${isChecked(opt.value) ? ' checked' : ''}`}>
                          {isChecked(opt.value) && <IconCheck size={10} stroke={3} />}
                        </span>
                      ) : (
                        <span className="ui-select-radio-space">
                          {isChecked(opt.value) && (
                            <IconCheck size={12} stroke={2.5} style={{ color: 'var(--orange)' }} />
                          )}
                        </span>
                      )}
                      {renderOption
                        ? renderOption(opt, { selected: isChecked(opt.value) })
                        : <span className="ui-select-opt-label">{opt.label}</span>}
                    </div>
                  ))}
                </div>
              ));
            })()}

            {asyncLoading && (
              <div className="ui-select-loader">
                <span className="ui-select-spinner" />
              </div>
            )}

            {!asyncLoading && visibleOptions.length === 0 && (
              <div className="ui-select-empty">
                {search ? 'Ничего не найдено' : 'Нет вариантов'}
              </div>
            )}
          </div>

          {/* Footer для multi */}
          {isMulti && (
            <div className="ui-select-footer">
              <button
                type="button"
                className="ui-select-footer-btn muted"
                onMouseDown={e => { e.preventDefault(); handleClearAll(); }}
              >
                Сбросить
              </button>
            </div>
          )}
        </div>
        );
        return usePortal ? createPortal(dropEl, document.body) : dropEl;
      })()}
    </div>
  );
};

export { Select };
